const $$splitComponentImporter = () => import('/app/apps/web/src/routes/_app/offers/index.tsx?tsr-split=component');
import { lazyRouteComponent } from '@tanstack/react-router';
const $$splitLoaderImporter = () => import('/app/apps/web/src/routes/_app/offers/index.tsx?tsr-split=loader');
import { lazyFn } from '@tanstack/react-router';
import { createFileRoute } from "@tanstack/react-router";
import { createTranslator } from "use-intl";
import { alternatesToLinks, buildCanonicalAlternates } from "@/i18n/alternates";
import { buildPagePreviewMetaTags } from "@/lib/buildPagePreviewMetaTags";
import { pickNumberArray } from "@/lib/faceted-search-url";
import { pickEnum, pickNumber, pickStringArray } from "@/lib/search-params";
import { getRootLoaderData } from "@/types/intl";
import { OFFER_YEAR_MAX, OFFER_YEAR_MIN } from "@garimpa/lib/buildOffersPageLink";
import { isCatalogId } from "@garimpa/lib/catalogId";
import { FORMAT_CATEGORIES_WITH_OTHER } from "@garimpa/lib/formatGroups";
import { DEFAULT_VIEWER_CURRENCY } from "@garimpa/lib/viewerPreferences";
import { offersPageSeoState } from "./offers-page-state";
const OFFER_SORTS = ["best_price", "price_drop", "recent"] as const;
const OFFER_FORMATS = ["all", ...FORMAT_CATEGORIES_WITH_OTHER] as const;
type OffersSearch = {
  condition?: "new" | "used";
  artistId?: string[];
  cursor?: number;
  format?: (typeof OFFER_FORMATS)[number];
  genre?: string[];
  /** Hidden minimum work popularity score for data inspection. */
  popularity?: number;
  priceMin?: number;
  priceMax?: number;
  sort?: (typeof OFFER_SORTS)[number];
  style?: string[];
  year?: number[];
};
/** Keeps the facet `year` shape: one exact year or a closed [from, to] range. */
function pickOfferYears(value: unknown) {
  const years = (pickNumberArray(value) ?? []).filter(year => Number.isInteger(year) && year >= OFFER_YEAR_MIN && year <= OFFER_YEAR_MAX).slice(0, 2);
  return years.length ? years : undefined;
}
export const Route = createFileRoute("/_app/offers/")({
  validateSearch: (raw: Record<string, unknown>): OffersSearch => {
    const artistIds = (pickStringArray(raw.artistId) ?? []).filter(isCatalogId).slice(0, 20);
    const cursor = pickNumber(raw.cursor);
    const popularity = pickNumber(raw.popularity);
    return {
      condition: pickEnum(raw.condition, ["new", "used"] as const),
      artistId: artistIds.length ? artistIds : undefined,
      cursor: cursor !== undefined && Number.isSafeInteger(cursor) && cursor >= 0 ? cursor : undefined,
      format: pickEnum(raw.format, OFFER_FORMATS),
      genre: pickStringArray(raw.genre),
      popularity: popularity !== undefined && popularity >= 0 ? popularity : undefined,
      priceMin: pickNumber(raw.priceMin),
      priceMax: pickNumber(raw.priceMax),
      sort: pickEnum(raw.sort, OFFER_SORTS),
      style: pickStringArray(raw.style),
      year: pickOfferYears(raw.year)
    };
  },
  // Visible offer filters stay client-reactive through React Query. A single artist
  // changes the page identity and SEO metadata, so artist changes rerun the loader.
  loaderDeps: ({
    search
  }) => ({
    artistIds: search.artistId,
    condition: search.condition
  }),
  loader: lazyFn($$splitLoaderImporter, 'loader'),
  head: async ({
    loaderData,
    match
  }) => {
    const {
      locale,
      messages,
      viewer
    } = getRootLoaderData(match);
    const search = match.search as OffersSearch;
    const seoInput = {
      ...search,
      artistName: loaderData?.artist?.name,
      currency: viewer?.preferences.currency
    };
    const translators = {
      offers: createTranslator({
        locale,
        messages,
        namespace: "shared.affiliates"
      }),
      collection: createTranslator({
        locale,
        messages,
        namespace: "shared.collection"
      }),
      genres: createTranslator({
        locale,
        messages,
        namespace: "shared.genres"
      }),
      styles: createTranslator({
        locale,
        messages,
        namespace: "shared.styles"
      })
    };
    const {
      buildOffersCanonicalPath,
      buildOffersSeoTitle,
      buildOffersSeoDescription,
      isIndexableOffers
    } = await import("@/lib/seo");
    const {
      canonicalPath,
      indexable
    } = offersPageSeoState({
      canonicalPath: buildOffersCanonicalPath(seoInput),
      indexable: isIndexableOffers(seoInput),
      invalidArtistFilter: loaderData?.invalidArtistFilter === true
    });
    const alts = buildCanonicalAlternates(canonicalPath);
    const title = buildOffersSeoTitle(seoInput, translators, locale);
    const description = buildOffersSeoDescription(seoInput, translators, locale);
    const previewSeoInput = {
      ...seoInput,
      currency: DEFAULT_VIEWER_CURRENCY
    };
    const previewTitle = buildOffersSeoTitle(previewSeoInput, translators, locale);
    const previewDescription = buildOffersSeoDescription(previewSeoInput, translators, locale);
    const jsonLd = indexable ? (await import("@/utils/jsonld")).buildCollectionPageJsonLd({
      name: title,
      description,
      url: canonicalPath
    }) : null;
    return {
      meta: await buildPagePreviewMetaTags({
        title,
        description,
        socialTitle: previewTitle,
        socialDescription: previewDescription,
        canonicalUrl: alts.canonical
      }, {
        robots: indexable ? null : "noindex,follow",
        openGraph: {},
        twitter: {}
      }, {
        locale
      }),
      links: alternatesToLinks(alts),
      scripts: jsonLd ? [{
        type: "application/ld+json",
        children: JSON.stringify(jsonLd)
      }] : undefined
    };
  },
  component: lazyRouteComponent($$splitComponentImporter, 'component')
});
type OfferFormat = (typeof OFFER_FORMATS)[number];